'use client';

import { useCallback } from 'react';
import {
    ADDED_TO_BASKET_MODAL_ID,
    Locale,
    Position,
    PRODUCT_INFO_QUICK_VIEW_MENU_ID,
    PRODUCT_NOTIFY_ME,
} from '@headless-workspace/config';
import { SideMenuModal, useModalActions } from '@headless-workspace/core-ui';
import { NotifyMeModal, QuickViewProductMenu } from '../../../client';
import type { VipBrandsValue } from '../../../server';
import { OpenModalParams, ProductModalHelpers } from '../productDetails/infos/selector/ProductModalHelpers';

export type UseProductModalDisplayReturnType = {
    openQuickViewModal: (productSku: string, vipBrands: VipBrandsValue, locale: Locale) => void;
    openNotifyMeModal: (productId: string, locale: Locale) => void;
    displayModal: (params: OpenModalParams) => void;
};

export const useProductModalDisplay = (): UseProductModalDisplayReturnType => {
    const { addIfAbsentAndOpen, close: closeMenuModal } = useModalActions();

    const openQuickViewModal = useCallback(
        (productSku: string, vipBrands: VipBrandsValue, locale: Locale) => {
            const content = <QuickViewProductMenu productSku={productSku} vipBrands={vipBrands} locale={locale} />;
            addIfAbsentAndOpen(
                PRODUCT_INFO_QUICK_VIEW_MENU_ID,
                SideMenuModal({
                    side: Position.Right,
                    map: { default: content },
                    className: 'flex flex-col overflow-hidden bg-background-l0',
                }),
            );
        },
        [addIfAbsentAndOpen],
    );

    const openNotifyMeModal = useCallback(
        (productId: string, locale: Locale) => {
            const content = <NotifyMeModal locale={locale} productIds={[productId]} />;
            addIfAbsentAndOpen(
                PRODUCT_NOTIFY_ME,
                SideMenuModal({ side: Position.Right, map: { default: content }, removeOnClose: true }),
            );
        },
        [addIfAbsentAndOpen],
    );

    const displayModal = useCallback(
        (params: OpenModalParams) => {
            const modalInfo = ProductModalHelpers.createModalOptions(params);
            if (modalInfo) {
                addIfAbsentAndOpen(modalInfo.modalId, modalInfo.modal);
            } else {
                closeMenuModal(ADDED_TO_BASKET_MODAL_ID, true);
            }
        },
        [addIfAbsentAndOpen, closeMenuModal],
    );

    return {
        openQuickViewModal,
        openNotifyMeModal,
        displayModal,
    };
};
